import React, { useState } from 'react';
import { MapPin, Navigation, Globe, Plus, X, Edit, Trash2, Printer, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { writeSanitizedHTML } from '../../utils/safePrint';

/**
 * LocationInformationForm Component
 * Handles the Incident Location Information form (part of ICS Status Summary)
 */
const LocationInformationForm = ({
  theme,
  // State from useIncidentLocation hook
  incidentLocations,
  currentIncidentLocation,
  setCurrentIncidentLocation,
  editingIncidentLocation,
  locationErrors,
  setLocationErrors,
  // Functions from useIncidentLocation hook
  addIncidentLocation,
  editIncidentLocation,
  cancelEditIncidentLocation,
  deleteIncidentLocation
}) => {
  // Expand/collapse tracking for individual location records
  const [expandedLocations, setExpandedLocations] = useState(new Set());
  const toggleLocationExpanded = (id) => {
    setExpandedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Province/State/Territory options
  const canadianProvinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
    'Newfoundland and Labrador', 'Nova Scotia', 'Ontario',
    'Prince Edward Island', 'Quebec', 'Saskatchewan'
  ];

  const canadianTerritories = [
    'Northwest Territories', 'Nunavut', 'Yukon'
  ];

  const usaStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const datumOptions = ['WGS84', 'NAD83', 'NAD27', 'Other'];

  const printAllLocations = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <html>
        <head>
          <title>Incident Location Information Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
            .header h1 { color: #f97316; margin: 0; font-size: 28px; }
            .location-item { margin-bottom: 30px; page-break-inside: avoid; border: 1px solid #ddd; border-radius: 8px; padding: 20px; }
            .section { margin-bottom: 20px; background: #f9f9f9; padding: 15px; border-radius: 5px; }
            .section h4 { color: #f97316; margin-top: 0; margin-bottom: 10px; }
            .field-group { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; color: #f97316; margin-bottom: 3px; font-size: 12px; }
            .field-value { color: #333; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Incident Location Information Report</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()} | <strong>Total Locations:</strong> ${incidentLocations.length}</p>
          </div>
          
          ${incidentLocations.map((loc, index) => `
            <div class="location-item">
              <h3 style="color: #f97316; margin-top: 0;">Location #${index + 1}</h3>
              
              <div class="section">
                <h4>Administrative Location</h4>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Province/State/Territory:</div>
                    <div class="field-value">${loc.provStateTerritory || 'N/A'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">County/Municipality:</div>
                    <div class="field-value">${loc.countyMunicipality || 'N/A'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">City:</div>
                    <div class="field-value">${loc.city || 'N/A'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Unit or Other:</div>
                    <div class="field-value">${loc.unitOrOther || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div class="section">
                <h4>Geographic Coordinates</h4>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Latitude:</div>
                    <div class="field-value">${loc.latitude || 'N/A'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Longitude:</div>
                    <div class="field-value">${loc.longitude || 'N/A'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Datum:</div>
                    <div class="field-value">${loc.datum || 'WGS84'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">UTM Coordinates:</div>
                    <div class="field-value">${loc.utmCoordinates || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div class="section">
                <h4>Additional Information</h4>
                <div class="field">
                  <div class="field-label">Jurisdiction:</div>
                  <div class="field-value">${loc.jurisdiction || 'N/A'}</div>
                </div>
                <div class="field">
                  <div class="field-label">Location Ownership:</div>
                  <div class="field-value">${loc.incidentLocationOwnership || 'N/A'}</div>
                </div>
                <div class="field">
                  <div class="field-label">Short Location Description:</div>
                  <div class="field-value">${loc.shortLocationDescription || 'N/A'}</div>
                </div>
                <div class="field">
                  <div class="field-label">Additional Electronic Geospatial Data:</div>
                  <div class="field-value">${loc.legalDescription || 'N/A'}</div>
                </div>
              </div>
            </div>
          `).join('')}
          
          <div class="footer">
            <p>Powered by EXRSIM - Emergency Response Simulation Platform</p>
          </div>
        </body>
      </html>
    `;
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  return (
    <div className="p-6 space-y-6 pb-20">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Incident Location Information</h1>
        <p className={`${theme.colors.textMuted} text-lg`}>
          Geographic coordinates, location details, and spatial information for comprehensive incident management
        </p>
      </div>

      {/* Location Form */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500">Incident Location Information</CardTitle>
          <CardDescription className={`${theme.colors.textMuted}`}>
            Enter geographic coordinates, administrative boundaries, and location descriptive information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Administrative Location Section */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-orange-500" />
              Administrative Location
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">
                  Province/State/Territory *
                </label>
                <select
                  value={currentIncidentLocation.provStateTerritory}
                  onChange={(e) => {
                    setCurrentIncidentLocation(prev => ({...prev, provStateTerritory: e.target.value}));
                    if (locationErrors.provStateTerritory) {
                      setLocationErrors(prev => ({...prev, provStateTerritory: null}));
                    }
                  }}
                  className={`w-full px-3 py-2 bg-gray-800 border ${locationErrors.provStateTerritory ? 'border-red-500' : 'border-gray-600'} text-white rounded-md focus:outline-none focus:border-orange-500`}
                >
                  <option value="">Select Province/State/Territory</option>
                  <optgroup label="Canadian Provinces">
                    {canadianProvinces.map(prov => (
                      <option key={prov} value={prov}>{prov}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Canadian Territories">
                    {canadianTerritories.map(terr => (
                      <option key={terr} value={terr}>{terr}</option>
                    ))}
                  </optgroup>
                  <optgroup label="USA States">
                    {usaStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </optgroup>
                </select>
                {locationErrors.provStateTerritory && (
                  <p className="text-red-500 text-sm mt-1">{locationErrors.provStateTerritory}</p>
                )}
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">County/Municipality</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.countyMunicipality}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, countyMunicipality: e.target.value}))}
                  placeholder="Enter county or municipality"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">City</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.city}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, city: e.target.value}))}
                  placeholder="Enter city name"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Unit or Other</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.unitOrOther}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, unitOrOther: e.target.value}))}
                  placeholder="Enter unit or other identifier"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Geographic Coordinates Section */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Navigation className="h-5 w-5 mr-2 text-orange-500" />
              Geographic Coordinates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Latitude</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.latitude}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, latitude: e.target.value}))}
                  placeholder="e.g., 45.4215"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Longitude</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.longitude}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, longitude: e.target.value}))}
                  placeholder="e.g., -75.6972"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Datum</label>
                <select
                  value={currentIncidentLocation.datum}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, datum: e.target.value}))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                >
                  {datumOptions.map(datum => (
                    <option key={datum} value={datum}>{datum}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">UTM Coordinates</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.utmCoordinates}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, utmCoordinates: e.target.value}))}
                  placeholder="e.g., 18T 446789 5028123"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Globe className="h-5 w-5 mr-2 text-orange-500" />
              Additional Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Jurisdiction</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.jurisdiction}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, jurisdiction: e.target.value}))}
                  placeholder="Enter jurisdiction"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Location Ownership</label>
                <Input
                  type="text"
                  value={currentIncidentLocation.incidentLocationOwnership}
                  onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, incidentLocationOwnership: e.target.value}))}
                  placeholder="Enter ownership details"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-white text-sm font-medium mb-2 block">Short Location Description</label>
              <Input
                type="text"
                value={currentIncidentLocation.shortLocationDescription}
                onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, shortLocationDescription: e.target.value}))}
                placeholder="Brief description of location"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
            <div className="mt-4">
              <label className="text-white text-sm font-medium mb-2 block">Note any additional electronic geospatial data</label>
              <textarea
                value={currentIncidentLocation.legalDescription}
                onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, legalDescription: e.target.value}))}
                placeholder="Data format, content, collection time information and / or labels"
                rows={3}
                className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
              />
            </div>
            <div className="mt-4 flex items-center">
              <input
                type="checkbox"
                id="geospatialData"
                checked={currentIncidentLocation.geospatialDataAvailable}
                onChange={(e) => setCurrentIncidentLocation(prev => ({...prev, geospatialDataAvailable: e.target.checked}))}
                className="mr-2 h-4 w-4"
              />
              <label htmlFor="geospatialData" className="text-white text-sm">
                Geospatial Data Available
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={addIncidentLocation} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              {editingIncidentLocation ? 'Update Location' : 'Add Location'}
            </Button>
            {editingIncidentLocation && (
              <Button onClick={cancelEditIncidentLocation} variant="outline" className="border-gray-500 text-gray-300">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Display Locations */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-orange-500">Location Records ({incidentLocations.length})</CardTitle>
          {incidentLocations.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setExpandedLocations(new Set(incidentLocations.map(l => l.id)))}
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                data-testid="location-expand-all"
              >
                Expand All
              </Button>
              <Button
                onClick={() => setExpandedLocations(new Set())}
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                data-testid="location-collapse-all"
              >
                Collapse All
              </Button>
              <Button 
                onClick={printAllLocations}
                variant="outline" 
                className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {incidentLocations.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No location records added yet</p>
              <p className="text-sm">Add your first location record above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incidentLocations.map(loc => {
                const isExpanded = expandedLocations.has(loc.id);
                return (
                <div key={loc.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        onClick={() => toggleLocationExpanded(loc.id)}
                        className="mt-1 p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-orange-400 transition-colors"
                        aria-label={isExpanded ? 'Collapse location details' : 'Expand location details'}
                        data-testid={`location-toggle-${loc.id}`}
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>
                      <div>
                        <h4 className="text-lg font-semibold text-white">
                          {loc.city || loc.countyMunicipality || 'Location'}, {loc.provStateTerritory}
                        </h4>
                        {loc.shortLocationDescription && (
                          <p className="text-gray-400 text-sm">{loc.shortLocationDescription}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => editIncidentLocation(loc)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => deleteIncidentLocation(loc.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {isExpanded && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <div className="text-orange-500 font-semibold mb-1 text-sm">Coordinates</div>
                      <div className="text-white text-sm">
                        {loc.latitude && loc.longitude 
                          ? `${loc.latitude}, ${loc.longitude}` 
                          : 'Not specified'}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1 text-sm">Datum</div>
                      <div className="text-white text-sm">{loc.datum || 'WGS84'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1 text-sm">Jurisdiction</div>
                      <div className="text-white text-sm">{loc.jurisdiction || 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1 text-sm">Ownership</div>
                      <div className="text-white text-sm">{loc.incidentLocationOwnership || 'Not specified'}</div>
                    </div>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LocationInformationForm;
